import path from "node:path";
import { exists, readJsonc, writeFileSafe } from "./filesystem.js";
import { createManagedBackup } from "./backup.js";
import { getTemplateFiles } from "./templates.js";

/**
 * @file opencode-merge.ts
 * @description Architecture & Registry for OpenCode configuration synthesis (`opencode.jsonc`).
 *
 * ## Architecture Overview
 * This module is responsible for initializing, updating, and merging the project's
 * `opencode.jsonc` file when `aw init` is executed in a consumer repository.
 *
 * ### Core Responsibilities:
 * 1. **Primary Agents Registry (`AGENTS`)**: Defines the 6 core workflow actors (Atlas, Nexus, Orion, Astra, Sage, Phoenix)
 *    and maps their instructions to `.ai-workflow/agents/<name>.md`.
 * 2. **Subagents Registry (`SUBAGENTS`)**: Registers domain-specific subagents mapped 1-to-1 with skills installed
 *    in `.ai-workflow/skills/<skill>/SKILL.md`. These subagents can be activated by OpenCode runtimes.
 * 3. **Commands Registry (`COMMANDS`)**: Maps slash-commands (e.g. `/atlas`, `/spec-create`, `/validate`) to their respective agent owners.
 * 4. **Safe JSONC Merging**: Merges AIWK configuration blocks into consumer repositories while preserving existing custom user
 *    settings in `opencode.jsonc` and managing backups.
 *
 * ### Registering a New Subagent:
 * To add a new subagent to the OpenCode runtime registry:
 * - Ensure a corresponding skill exists in `dist-assets/skills/<skill-name>/SKILL.md`.
 * - Add a new entry to the `SUBAGENTS` object with a PascalCase/Kebab name, a concise `description`, and the matching `skill` directory name.
 * - Note: 5 auxiliary skills (`cyber-security`, `database`, `devops`, `localization`, `performance`) are intentionally excluded
 *   from subagent registration as they serve as contextual lenses for existing agents (see `dist-assets/AGENTS.md`).
 */

const LEGACY_MARKER = "_aiWorkflowManaged";

interface AgentDef {
  description: string;
  mode: "primary" | "all";
}

const AGENTS: Record<string, AgentDef> = {
  Atlas: { description: "Router and workflow coordinator for AI Workflow Kit", mode: "primary" },
  Nexus: { description: "Discovery, requirement, scope, and specification owner", mode: "all" },
  Orion: { description: "Planning, PR sequencing, release, and deployment strategy owner", mode: "all" },
  Astra: { description: "Implementation owner for incremental, scoped workspace changes", mode: "all" },
  Sage: { description: "Audit, validation, evidence, and quality gate owner", mode: "all" },
  Phoenix: { description: "Bounded remediation and regression recovery owner", mode: "all" }
};

interface SubagentDef {
  description: string;
  skill: string;
}

const SUBAGENTS: Record<string, SubagentDef> = {
  "Architecture-Specialist": { description: "Capability only: advise on the simplest safe architecture for current requirements", skill: "architecture" },
  "Backend-Engineer": { description: "Capability only: advise on PHP, Node, or Python APIs and persistence", skill: "backend-development" },
  "Deployment-Specialist": { description: "Capability only: analyze deployment, release, and production readiness", skill: "deployment" },
  "Design-Specialist": { description: "Capability only: advise on simple and maintainable design principles", skill: "design-principles" },
  "Docs-Engineer": { description: "Capability only: advise on documentation, READMEs, guides, and prompts", skill: "documentation" },
  "Frontend-Engineer": { description: "Capability only: advise on UI, state, routing, and accessibility", skill: "frontend-development" },
  "Frontend-Design-System-Specialist": { description: "Capability only: review CSS and HTML tokens, semantics, typography, and visual quality", skill: "frontend-design-system" },
  "Full-Stack-Engineer": { description: "Capability only: analyze frontend and backend integration as one testable increment", skill: "full-stack-development" },
  "Token-Economist": { description: "Capability only: analyze context size while preserving safe execution information", skill: "optimize-tokens" },
  "PR-Manager": { description: "Capability only: plan or review small pull requests while preserving scope", skill: "pr-workflow" },
  "Discovery-Analyst": { description: "Capability only: clarify ambiguous requests into actionable outcomes", skill: "product-discovery" },
  "Product-Planner": { description: "Capability only: shape clarified needs into scoped requirements", skill: "product-planning" },
  "Memory-Guardian": { description: "Capability only: advise on durable project decisions and recurring corrections", skill: "project-memory" },
  "Prompt-Engineer": { description: "Capability only: advise on prompts for agents and workflows", skill: "prompt-engineer" },
  "QA-Engineer": { description: "Capability only: design or review tests, acceptance criteria, and validation evidence", skill: "qa-workflow" },
  "Refactoring-Specialist": { description: "Capability only: review structure and behavior-preserving simplifications", skill: "refactoring" },
  "Release-Specialist": { description: "Capability only: analyze release readiness with explicit gates and evidence", skill: "release-workflow" },
  "SDD-Specialist": { description: "Capability only: shape approved requirements into testable specifications", skill: "spec-driven-development" },
  "Technical-Leader": { description: "Capability only: analyze technical decisions, architecture, and trade-offs", skill: "technical-leadership" },
  "UI-UX-Engineer": { description: "Capability only: review usability and interface clarity", skill: "ui-ux-design" }
};

interface CommandDef {
  description: string;
  agent: string;
}

const COMMANDS: Record<string, CommandDef> = {
  atlas: { description: "Decide safest next step and delegate to the appropriate workflow or agent", agent: "Atlas" },
  run: { description: "Execute the master orchestration pipeline (Gates, SDD, Implementation, Validation, Healing)", agent: "Atlas" },
  discover: { description: "Understand the project, gather context, and create initial documentation", agent: "Nexus" },
  "spec-create": { description: "Create a clear specification before implementation", agent: "Nexus" },
  "spec-review": { description: "Review a specification and validate scope, risks, criteria, and gaps", agent: "Nexus" },
  "spec-implement": { description: "Implement an approved specification incrementally", agent: "Astra" },
  plan: { description: "Plan a task or change, defining steps, risks, and validation", agent: "Orion" },
  implement: { description: "Implement a small task with defined scope", agent: "Astra" },
  validate: { description: "Run project validations and collect evidence", agent: "Sage" },
  audit: { description: "Audit the project, identify issues, and suggest prioritized improvements", agent: "Sage" },
  "optimize-tokens": { description: "Analyze workflow and context usage; suggest reductions without losing quality or evidence", agent: "Sage" },
  "update-memory": { description: "Update durable project memory with relevant decisions, constraints, and reusable context", agent: "Nexus" },
  release: { description: "Prepare delivery with commit, push, PR/merge readiness, changelog, and evidence", agent: "Orion" },
  deploy: { description: "Publish the project to the defined environment after validation and release readiness", agent: "Orion" }
};

interface ManagedAgentConfig {
  mode: "primary" | "subagent" | "all";
  description: string;
  prompt: string;
  permission?: {
    edit?: "allow" | "ask" | "deny";
    bash?: "allow" | "ask" | "deny";
    task?: Record<string, "allow" | "ask" | "deny">;
  };
}

interface ManagedCommandConfig {
  description: string;
  agent: string;
  template: string;
}

interface ManagedConfig {
  $schema: string;
  default_agent: string;
  agent: Record<string, ManagedAgentConfig>;
  skills: {
    paths: string[];
  };
  command: Record<string, ManagedCommandConfig>;
  mcp?: Record<string, any>;
}

function buildManagedConfig(): ManagedConfig {
  const agent: Record<string, ManagedAgentConfig> = {};
  const coreTaskPermissions = Object.fromEntries([
    ["*", "deny"],
    ...Object.keys(AGENTS).filter((name) => name !== "Atlas").map((name) => [name, "allow"])
  ]) as Record<string, "allow" | "deny">;
  const capabilityTaskPermissions = Object.fromEntries([
    ["*", "deny"],
    ...Object.keys(SUBAGENTS).map((name) => [name, "allow"])
  ]) as Record<string, "allow" | "deny">;

  for (const [name, def] of Object.entries(AGENTS)) {
    agent[name] = {
      mode: def.mode,
      description: def.description,
      prompt: `{file:./.ai-workflow/opencode/agents/${name.toLowerCase()}.md}`,
      permission: {
        ...(name === "Atlas" ? { edit: "deny" as const, bash: "ask" as const } : {}),
        ...(name === "Astra" ? { edit: "allow" as const } : {}),
        task: name === "Atlas" ? coreTaskPermissions : capabilityTaskPermissions
      }
    };
  }

  for (const [name, def] of Object.entries(SUBAGENTS)) {
    agent[name] = {
      mode: "subagent",
      description: def.description,
      prompt: `{file:./.ai-workflow/opencode/skills/${def.skill}/SKILL.md}`,
      permission: {
        edit: "deny",
        bash: "ask",
        task: { "*": "deny" }
      }
    };
  }

  const command: Record<string, ManagedCommandConfig> = {};
  for (const [name, def] of Object.entries(COMMANDS)) {
    command[name] = {
      description: def.description,
      agent: def.agent,
      template: `{file:./.ai-workflow/opencode/commands/${name}.md}`
    };
  }

  return {
    $schema: "https://opencode.ai/config.json",
    default_agent: "Atlas",
    agent,
    skills: {
      paths: ["./.ai-workflow/opencode/skills"]
    },
    command
  };
}

export interface MergeOpencodeConfigOptions {
  force?: boolean;
  backupRoot?: string;
}

export interface MergeOpencodeConfigResult {
  changed: boolean;
  reason: string;
}

export async function mergeOpencodeConfig(
  cwd: string,
  { force = false, backupRoot = ".ai-workflow-backups" }: MergeOpencodeConfigOptions = {}
): Promise<MergeOpencodeConfigResult> {
  const targetPath = path.join(cwd, "opencode.jsonc");
  const managed = buildManagedConfig();

  if (!(await exists(targetPath))) {
    await writeFileSafe(targetPath, `${JSON.stringify(managed, null, 2)}\n`);
    return { changed: true, reason: "created" };
  }

  let current: any;
  try {
    current = await readJsonc(targetPath);
  } catch {
    if (!force) {
      return { changed: false, reason: "invalid-json (skipped)" };
    }
    await createManagedBackup(targetPath, {
      cwd,
      backupRoot,
      maxPerFile: 20
    });
    await writeFileSafe(targetPath, `${JSON.stringify(managed, null, 2)}\n`);
    return { changed: true, reason: "invalid-json (replaced with --force)" };
  }

  const { [LEGACY_MARKER]: _legacyManaged, ...currentWithoutLegacy } = current;

  const next = {
    ...currentWithoutLegacy,
    $schema: current.$schema ?? managed.$schema,
    ...(managed.mcp
      ? {
          mcp: {
            ...(current.mcp ?? {}),
            ...managed.mcp
          }
        }
      : {}),
    agent: {
      ...(current.agent ?? {}),
      ...managed.agent
    },
    command: {
      ...(current.command ?? {}),
      ...managed.command
    }
  };

  const changed = JSON.stringify(current) !== JSON.stringify(next);
  if (!changed) {
    return { changed: false, reason: "already-up-to-date" };
  }

  await writeFileSafe(targetPath, `${JSON.stringify(next, null, 2)}\n`);
  return { changed: true, reason: "merged-managed-sections" };
}
